var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icons } from 'src/ui/components/Icons/Icons';
import { useMount } from 'src/shared/hooks/useLifecycle';
import { onClickOutside } from 'src/shared/helpers/viewsHelpers';
import { windowExist } from 'src/shared/constants';
import { E_ICONS } from 'src/ui/components/Icons/constants';
import { E_SELECT_SHORTCUTS, IDENTIFIER_SELECT, IDENTIFIER_SELECT_CATEGORY_LABEL, IDENTIFIER_SELECT_ERROR, IDENTIFIER_SELECT_OPTION_ITEM, IDENTIFIER_SELECT_OPTION_SHORTCUT, IDENTIFIER_SELECT_OPTIONS, IDENTIFIER_SELECT_VALUE_SELECTED, } from 'src/ui/components/Input/Select/constants';
import { getPropsByKeyPath } from 'src/shared/helpers/objHelpers';
import { buildSelectValueAllSelected, buildSelectValueNoneSelected, buildValueAfterDeselectingOption, buildValueAfterSelectingOption, ensureObjectOptions, isOptionSelected, } from './selectHelpers';
import { isMapOfValuesByFieldName, isPrimitiveArrayValue, isPrimitiveValue, isStringArrayOptions, } from './selectTypes';
import styles from './Select.module.scss';
export var Select = function (props) {
    var _a, _b, _c, _d, _e, _f;
    var i18n = props.i18n, label = props.label, options = props.options, multiple = props.multiple, _g = props.withShortcut, withShortcut = _g === void 0 ? false : _g, _h = props.value, value = _h === void 0 ? [] : _h, className = props.className, withInfo = props.withInfo, isError = props.isError, infoMessage = props.infoMessage, _j = props.disabled, disabled = _j === void 0 ? false : _j, isOnTop = props.isOnTop, isLabelOnTop = props.isLabelOnTop, defaultValueProps = props.defaultValue, onChange = props.onChange;
    var _k = useState(false), isOpen = _k[0], setIsOpen = _k[1];
    var _l = useState(isOnTop), toTop = _l[0], setToTop = _l[1];
    var _m = useState(E_SELECT_SHORTCUTS.DEFAULT), currentShortcut = _m[0], setCurrentShortcut = _m[1];
    var optionsWrapperRef = useRef(null);
    var selectedValueRef = useRef(null);
    var selectListRef = useRef(null);
    var defaultValue = useRef(defaultValueProps);
    useMount(function () {
        onClickOutside(optionsWrapperRef, function () { return setIsOpen(false); });
    });
    useEffect(function () {
        var updateOnResize = function () {
            if (!windowExist || !selectedValueRef.current || !selectListRef.current) {
                return;
            }
            var toggleRect = selectedValueRef.current.getBoundingClientRect();
            var toggleTop = toggleRect.top;
            var toggleBottom = toggleRect.bottom;
            var availableHeightOnTop = toggleTop;
            var availableHeightAtTheBottom = window.innerHeight - toggleBottom;
            var shouldSetOnTop = availableHeightOnTop > availableHeightAtTheBottom;
            if (shouldSetOnTop !== isOnTop) {
                setToTop(shouldSetOnTop);
            }
            // const availableHeight = shouldSetOnTop ? availableHeightOnTop : availableHeightAtTheBottom
            // const maxHeight = Math.min(0.6 * window.innerHeight, availableHeight - 20)
            // selectListRef.current.style.maxHeight = `${maxHeight}px`
        };
        window.addEventListener('resize', updateOnResize);
        updateOnResize();
        return function () {
            window.removeEventListener('resize', updateOnResize);
        };
    }, []);
    var buildOptionsElements = function (inputOptions) {
        var rootOption = ensureObjectOptions(inputOptions);
        var fieldNames = rootOption.fieldNames, options = rootOption.options;
        return buildOptionsElementsFromOptionList(options, 0, fieldNames);
    };
    var buildOptionsElementsFromOptionList = function (options, level, fieldNames) {
        if (isStringArrayOptions(options)) {
            return options.map(function (optValue) { return buildOptionsElement(optValue, level, fieldNames); });
        }
        return options.map(function (option) {
            var subOptionFieldNames = option.fieldNames, subOptionsLabel = option.label, subOptions = option.options;
            var optionFieldNames = subOptionFieldNames || fieldNames;
            return (_jsxs("li", __assign({ "data-test": IDENTIFIER_SELECT_OPTION_ITEM }, { children: [_jsx("div", __assign({ "data-test": IDENTIFIER_SELECT_CATEGORY_LABEL, className: styles.optionCategoryLabel }, { children: subOptionsLabel })), _jsx("div", { className: styles.optionCategorySeparator }), _jsx("ul", __assign({ "data-test": IDENTIFIER_SELECT_OPTIONS }, { children: buildOptionsElementsFromOptionList(subOptions, level + 1, optionFieldNames) }))] }), subOptionsLabel));
        });
    };
    var buildOptionsElement = function (optionValue, level, fieldNames) {
        var isSelected = isOptionSelected(value, optionValue, fieldNames);
        var paddingLevel = level + 1;
        var itemStyle = {
            paddingLeft: paddingLevel * 12,
        };
        return (_jsx("li", __assign({ "data-test": IDENTIFIER_SELECT_OPTION_ITEM, className: optionClassName(isSelected), style: itemStyle, onClick: function () { return handleOnOptionClick(optionValue, fieldNames); } }, { children: optionValue }), optionValue.toString()));
    };
    var handleOnOptionClick = function (optionValue, fieldNames) {
        var isAlreadySelected = isOptionSelected(value, optionValue, fieldNames);
        if (isAlreadySelected) {
            deselectOption(optionValue, fieldNames);
        }
        else {
            selectOption(optionValue, fieldNames);
        }
        if (!multiple) {
            setIsOpen(false);
        }
    };
    var handleOpenOptions = function () {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };
    var handleShortcutClick = function (e) {
        e.stopPropagation();
        var nextShortcut = chooseNextShortcut(currentShortcut);
        var newValues = computeValueForShortcut(nextShortcut);
        setCurrentShortcut(nextShortcut);
        onChange(newValues);
    };
    var chooseNextShortcut = function (shortcut) {
        var _a;
        switch (shortcut) {
            case E_SELECT_SHORTCUTS.DEFAULT: {
                return E_SELECT_SHORTCUTS.ALL;
            }
            case E_SELECT_SHORTCUTS.ALL: {
                return E_SELECT_SHORTCUTS.NONE;
            }
            case E_SELECT_SHORTCUTS.NONE: {
                return ((_a = defaultValue.current) === null || _a === void 0 ? void 0 : _a.length) > 0 ? E_SELECT_SHORTCUTS.DEFAULT : E_SELECT_SHORTCUTS.ALL;
            }
        }
    };
    var computeValueForShortcut = function (shortcut) {
        switch (shortcut) {
            case E_SELECT_SHORTCUTS.DEFAULT: {
                return defaultValue.current;
            }
            case E_SELECT_SHORTCUTS.ALL: {
                return buildSelectValueAllSelected(options);
            }
            case E_SELECT_SHORTCUTS.NONE: {
                return [];
            }
        }
    };
    var selectOption = function (optionValue, fieldNames) {
        var prevValue = multiple ? value : buildSelectValueNoneSelected(options, multiple);
        var newValue = buildValueAfterSelectingOption(prevValue, optionValue, fieldNames);
        onChange(newValue);
    };
    var deselectOption = function (optionValue, fieldNames) {
        var newValue = buildValueAfterDeselectingOption(value, optionValue, fieldNames);
        onChange(newValue);
    };
    var buildOverviewTextFromValue = function (value) {
        if (isPrimitiveValue(value)) {
            return value.toString();
        }
        var values = [];
        if (isPrimitiveArrayValue(value)) {
            values = value;
        }
        else if (isMapOfValuesByFieldName(value)) {
            var mergedValues = Object.values(value).flatMap(function (values) { return values; });
            values = mergedValues.filter(function (value, index, self) { return self.indexOf(value) === index; });
        }
        return values.join(', ');
    };
    var selectWrapperClassName = classNames(styles.selectWrapper, (_a = {}, _a[styles.selectWrapperWithNoLabel] = !!!label, _a), className);
    var arrowClassName = classNames(styles.arrow, (_b = {},
        _b[styles.arrowForOpen] = isOpen,
        _b));
    var optionsWrapperClassName = classNames(styles.optionsWrapper, (_c = {},
        _c[styles.optionsWrapperOpen] = isOpen,
        _c[styles.optionsWrapperOpenToTop] = toTop,
        _c[styles.optionsWrapperOpenToTopWithError] = withInfo,
        _c));
    var labelClassName = classNames(styles.label, (_d = {},
        _d[styles.labelOnTop] = isLabelOnTop || (Array.isArray(value) ? value.length > 0 : !!value) || isOpen,
        _d));
    var optionClassName = function (isSelected) {
        var _a;
        return classNames(styles.optionItem, (_a = {}, _a[styles.optionItemSelected] = isSelected, _a));
    };
    var overViewValueWrapperClassName = classNames(styles.overviewValuesWrapper, (_e = {},
        _e[styles.overViewValueWrapperSelected] = isOpen,
        _e[styles.inputValueError] = !!withInfo && !!isError,
        _e[styles.inputValueDisabled] = disabled,
        _e), 'select-overview');
    var overviewText = buildOverviewTextFromValue(value);
    var getShortcutLabel = function () {
        var _a;
        if (!i18n) {
            console.error('i18n is not defined');
            return '';
        }
        switch (currentShortcut) {
            case E_SELECT_SHORTCUTS.DEFAULT: {
                return getPropsByKeyPath(i18n.keyValueMap, 'select.all');
            }
            case E_SELECT_SHORTCUTS.ALL: {
                return getPropsByKeyPath(i18n.keyValueMap, 'select.none');
            }
            case E_SELECT_SHORTCUTS.NONE: {
                if (((_a = defaultValue.current) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    return getPropsByKeyPath(i18n.keyValueMap, 'select.default');
                }
                else {
                    return getPropsByKeyPath(i18n.keyValueMap, 'select.all');
                }
            }
        }
    };
    var infoClassName = classNames(styles.infoMessage, (_f = {}, _f[styles.infoMessageError] = isError, _f));
    var optionElements = buildOptionsElements(options);
    return (_jsxs("div", __assign({ "data-test": IDENTIFIER_SELECT, className: selectWrapperClassName, ref: optionsWrapperRef }, { children: [_jsxs("div", __assign({ "data-test": IDENTIFIER_SELECT_VALUE_SELECTED, className: overViewValueWrapperClassName, onClick: handleOpenOptions, ref: selectedValueRef }, { children: [_jsx("p", __assign({ className: styles.overviewValues }, { children: overviewText })), label && _jsx("p", __assign({ className: labelClassName }, { children: label })), _jsx(Icons, { className: arrowClassName, icon: E_ICONS.ARROW_RIGHT })] })), withInfo && (_jsxs("div", __assign({ "data-test": IDENTIFIER_SELECT_ERROR, className: infoClassName, style: { opacity: infoMessage ? 1 : 0 } }, { children: [infoMessage && _jsx(Icons, { icon: E_ICONS.INFO }), infoMessage && _jsx("p", { children: infoMessage })] }))), _jsxs("ul", __assign({ "data-test": IDENTIFIER_SELECT_OPTIONS, ref: selectListRef, className: optionsWrapperClassName }, { children: [withShortcut && multiple && (_jsxs(_Fragment, { children: [_jsx("li", __assign({ "data-test": IDENTIFIER_SELECT_OPTION_SHORTCUT, className: classNames(styles.optionItem, styles.shortcutItem), onClick: handleShortcutClick }, { children: getShortcutLabel() })), _jsx("li", { className: styles.shortcutItemSeparator })] })), optionElements] }))] })));
};
